import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { $public } from "@/services";
import Router from "next/router";
import Box from "@mui/material/Box";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween"; // ES 2015
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import DOMPurify from "dompurify";
import { RRule, RRuleSet, Weekday } from "rrule";
import { SelectOptions } from "@/component/input";
import { Visit, VisitStatus } from "@/interfaces/IServiceUser";
import {
  DayTime,
  IDEAL_TIME,
  TaskManagementResponse,
} from "@/interfaces/ITaskManagement";
import {
  ALERT_PRIORITY,
  ALERT_REFERENCE,
  ALERT_STATUS,
  DEFAULT_DATE_FORMAT,
} from "./constants";
import logger from "../runtime-utils/observability/logger";
import { CustomQuestion, QuestionType } from "@/interfaces/ICarePlanning";
import { IAlert } from "@/interfaces/IAlert";

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const isNotSSR = typeof window !== "undefined";

export default function calculateAge(birthday: dayjs.Dayjs | null) {
  const currentDate = dayjs();
  const yearsDiff = currentDate.diff(birthday, "year");
  birthday = birthday && birthday.add(yearsDiff, "year");
  const monthsDiff = currentDate.diff(birthday, "month");
  return `${yearsDiff} years and ${monthsDiff} months`;
}

/** Select a valid HTML input type for numeric or formatted age values. */
export const getAgeInputType = (
  dateOfBirth: unknown,
  age: unknown
): "number" | "text" => {
  if (dateOfBirth) return "text";
  const ageValue = String(age ?? "").trim();
  return ageValue && !/^\d+$/.test(ageValue) ? "text" : "number";
};

export const convertToTitleCase = (str: string | undefined) => {
  if (!str) return "";
  return str
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(str.includes("-") ? "-" : str.includes("_") ? "_" : " ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const scrollToFirstError = (ref: any) => {
  const firstErrorField =
    ref.current && ref.current.querySelector(".Mui-error");
  if (firstErrorField) {
    firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

export function dateFormat(date: Date): string {
  if (!date) return "";
  const newDate = dayjs(date).format(DEFAULT_DATE_FORMAT);
  return newDate;
}

export const getInitials = (name: string) => {
  if (!name) return "";
  const initials = name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("");
  return initials;
};

export const getPlaceholder = (label: string) => (
  <Box
    sx={{
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "#cccccc",
      display: "grid",
      placeItems: "center",
      fontSize: "16px",
      fontWeight: "500",
      margin: "0.6rem 0",
      color: "#00261C",
      textTransform: "uppercase",
    }}
  >
    {label}
  </Box>
);

export const isLocalhost = Boolean(
  (isNotSSR && window.location.hostname === "localhost") ||
  // [::1] is the IPv6 localhost address.
  (isNotSSR && window.location.hostname === "[::1]") ||
  // 127.0.0.0/8 are considered localhost for IPv4.
  (isNotSSR &&
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    ))
);

export const getDayFromDate = (date: string | Date | undefined) => {
  if (!date) return "";

  return dayjs(date).format("ddd");
};

export const formatDateWithTime = (
  originalDate: string | Date | Dayjs | undefined
) => {
  if (!originalDate) return "";
  const date = dayjs(originalDate);

  const formattedDate = date.format(DEFAULT_DATE_FORMAT);
  const formattedTime = date.format("HH:mm");
  return `${formattedDate} at ${formattedTime}`;
};

export const getInitialURL = (url: string) => {
  const parts = url.split("/");
  const I = parts?.[1];
  const II = parts?.[2];

  let final = "";
  if (I) final += `${I}`;
  if (II) final += `/${II}`;

  return final;
};

export const fetchFileContent = async (fileUrl: string) => {
  const fileName = fileUrl.split("/").pop() || "";
  try {
    const response = await fetch($public(fileUrl));
    const fileData = await response.blob();

    // Create a File object with the fetched data
    const file = new File([fileData], fileName);

    // Set the initial files state with the created File object
    return file;
  } catch (error) {
    logger.error("Error fetching file:", error);
  }
};

export const openDoc = async (doc: string) => {
  const normalizedDoc = doc.replace(/(\/uploads)+/g, "/uploads");
  const link = document.createElement("a");
  link.target = "_blank";
  link.href = $public(normalizedDoc);
  logger.debug("link", link.href);
  link.click();
  link.remove();
};

export const convertCamelCaseToSentenceCase = (str: string) => {
  if (!str) return str;
  if (str.includes(" ")) return str;
  const result = str.replace(/([A-Z])/g, " $1");
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult.trim();
};

export const $capitalize = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Function to export data as CSV

export const exportToCSV = (
  jsonData: any[],
  filename: string,
  filters?: string[]
) => {
  if (!jsonData || jsonData.length === 0) {
    logger.error("No data provided for CSV export.");
    return;
  }

  // Get headers from the first item
  let headers = Object.keys(jsonData[0]);
  if (filters && filters.length > 0) {
    headers = headers.filter((header) => !filters.includes(header));
  }

  // Quote every header so column names with spaces (e.g. "Service User",
  // "Visit Date") are treated as a single column and not split by Excel.
  let csv =
    headers
      .map(
        (header) =>
          '"' + convertCamelCaseToSentenceCase(header).replace(/"/g, '""') + '"'
      )
      .join(",") + "\n";

  for (let i = 0; i < jsonData.length; i++) {
    const row = [];
    for (let j = 0; j < headers.length; j++) {
      let value = jsonData[i][headers[j]];
      // If service user is genuinely empty/missing, show as "undefined"
      const isServiceUserCol =
        headers[j] === "Service User" || headers[j] === "serviceUser";
      if (
        isServiceUserCol &&
        (value === "" || value === null || value === undefined)
      ) {
        value = "undefined";
      } else if (value === null || value === undefined) {
        value = "";
      }
      if (headers[j] === "createdAt" || headers[j] === "updatedAt") {
        value = formatDateWithTime(value);
      }
      if (typeof value === "string") value = value.replace(/"/g, '""'); // Escape double quotes
      row.push('"' + value + '"');
    }
    csv += row.join(",") + "\n";
  }

  // Create a Blob and trigger download
  const blob = new Blob([csv], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

// Function to export data as PDF
export const exportToPDF = (
  data: any[],
  filename: string,
  filters?: string[]
) => {
  const doc = new jsPDF({
    orientation: "landscape", // Set orientation to landscape
  });

  // Convert JSON data to PDF format
  let headers = Object.keys(data[0]);
  if (filters && filters.length > 0) {
    headers = headers.filter((header) => !filters.includes(header));
    data = data.map((item) => {
      const newItem: any = {};
      headers.forEach((header) => {
        if (header === "createdAt" || header === "updatedAt") {
          return (newItem[header] = formatDateWithTime(item[header]));
        }
        newItem[header] = item[header];
      });
      return newItem;
    });
  }
  const rows = data.map((item) => Object.values(item));

  autoTable(doc, {
    head: [headers.map((header) => convertCamelCaseToSentenceCase(header))],
    body: rows,
    willDrawPage: () => {
      doc.setFontSize(16);
      doc.text(filename.split(".")[0], 20, 20);
    },
  });

  // Save the PDF file
  doc.save(filename);
};

export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  timeout: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } => {
  let timer: ReturnType<typeof setTimeout> | null;

  const debouncedFunc = function (this: any, ...args: Parameters<T>) {
    if (timer) clearTimeout(timer);

    timer = setTimeout(() => {
      timer = null;
      func.apply(this, args);
    }, timeout);
  };

  debouncedFunc.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return debouncedFunc;
};

export const fetchRemoteFileAsBlob = async (url: string) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
};

// utils/helpers.ts
export const dataURLtoBlob = (dataURL: string, name: string) => {
  const arr = dataURL.split(",");
  const mime = arr[0]?.match(/:(.*?);/)?.[1] as string;
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], name, { type: mime });
};

export const convertToFormData = (data: any) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]: any) => {
    if (typeof value === "object") {
      if (Array.isArray(value) && value.every((item) => item instanceof File)) {
        value.forEach((item: any, index: number) => {
          formData.append(`${key}[${index}]`, item);
        });
      }
      if (value instanceof File) {
        formData.append(key, value);
      } else if (dayjs.isDayjs(value) || value instanceof Date) {
        formData.append(key, value as unknown as string);
      } else if (value === null || value === undefined) {
        formData.append(key, "");
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else if (value instanceof File) {
      formData.append(key, value);
    } else if (key === "email") {
      formData.append(key, value.toLowerCase());
    } else {
      formData.append(key, value);
    }
  });
  return formData;
};

export const isObjectEmpty = (obj: any) => obj && Object.keys(obj).length === 0;

export function getValues<T extends SelectOptions>(arr: T[]): string[] {
  if (!arr) return [];
  return arr.map((item) => item?.value);
}

export const getChartType = (type: string) => {
  if (!type) return "scatter";
  if (
    [
      "must",
      "blood-oxygen",
      "Heart Rate",
      "mobility",
      "mood",
      "respiratory-rate",
      "stool",
      "temperature",
      "waterlow",
      "weight",
    ].includes(type)
  ) {
    return "line";
  }
  if (
    [
      "drink",
      "snack",
      "meal",
      "pudding",
      "peg-feed",
      "medication-round",
    ].includes(type)
  ) {
    return "bar";
  }
  return "scatter";
};

export const removeDuplicates = (arr: any[], key: string | undefined) => {
  const seen = new Set();
  return arr.filter((el) => {
    const duplicate = seen.has(key ? el[key] : el);
    seen.add(key ? el[key] : el);
    return !duplicate && (key ? el[key] : el);
  });
};

export const checkInputsAndSetSection = (
  inputs: string[],
  errs: any,
  setSection: (value: React.SetStateAction<boolean>) => void
) => {
  if (inputs.some((item) => Object.keys(errs).includes(item))) {
    setSection(true);
  }
};

export function extractPlainTextFromRichText(richText: string): string {
  if (!richText) return "";
  // Sanitize HTML before parsing
  const cleanHTML = DOMPurify.sanitize(richText);
  // Create a DOMParser instance and parse the rich text as HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanHTML, "text/html");

  // Use a recursive function to extract text nodes from the parsed HTML
  function extractText(node: Node): string {
    let result = "";
    if (node.nodeType === Node.TEXT_NODE) {
      // If the node is a text node, extract its text content
      result += node.textContent || "";
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // If the node is an element, recursively extract text from its children
      const children = node.childNodes;
      for (let i = 0; i < children.length; i++) {
        result += extractText(children[i]);
      }
    }
    return result;
  }

  // Start extracting text from the root of the parsed HTML
  const plainText = extractText(doc.body);

  // Normalize whitespace and trim the resulting plain text
  return plainText.replace(/\s+/g, " ").trim();
}

export function levenshteinDistance(s1: string, s2: string) {
  const len1 = s1.length;
  const len2 = s2.length;

  const matrix = [];

  // initialize matrix
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // calculate Levenshtein distance
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[len1][len2];
}

export function sortBySimilarity(strings: string[], targetString: string) {
  return strings.sort((a, b) => {
    const distanceA = levenshteinDistance(a, targetString);
    const distanceB = levenshteinDistance(b, targetString);
    return distanceA - distanceB;
  });
}

export const getAllDatesInRange = (
  startDate: Dayjs | null,
  endDate: Dayjs | null
): Dayjs[] => {
  if (!startDate || !endDate) return [];
  const dates = [];
  let currentDate = startDate;
  while (currentDate.isSameOrBefore(endDate)) {
    dates.push(currentDate);
    currentDate = currentDate.add(1, "day");
  }
  return dates;
};

export const expandRecurringEvents = (
  visit: any,
  startRange: Date,
  endRange: Date
) => {
  const options = getRRuleAsObject(visit);
  if (!(options as any).freq || !(options as any).dtstart) return [];
  const rrule = new RRule(options as any);
  const start = new Date(visit.start);
  const end = new Date(visit.end);
  const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // duration in hours

  const occurrences = rrule.between(startRange, endRange, true);

  return occurrences.map((occurrence: Date) => ({
    ...visit,
    start: new Date(occurrence),
    end: new Date(occurrence.getTime() + duration * 60 * 60 * 1000),
  }));
};

export const convertMinutesToReadableTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  const isPluralMins = mins > 1 ? "s" : "";
  const isPluralHours = hours > 1 ? "s" : "";

  if (hours === 0) {
    return `${mins}min${isPluralMins}`;
  }

  return `${hours}hour${isPluralHours} ${mins}min${isPluralMins}`;
};

/*
 * Function to modify the date and time of a given date object
 * @param date - Date object to modify
 * @param time - Date object containing the time to set
 * @returns - Modified Date object with the time set
 */
export const modifyDateTimes = (date: Date, time: Date) => {
  const newDate = new Date(date);
  const newTime = new Date(time);
  newDate.setHours(newTime.getHours());
  newDate.setMinutes(newTime.getMinutes());
  newDate.setSeconds(newTime.getSeconds());
  newDate.setMilliseconds(newTime.getMilliseconds());
  return newDate;
};

export const visitColorLookup = {
  [VisitStatus.UN_ALLOCATED]: "#9F9F9E",
  [VisitStatus.SCHEDULED]: "#8824B6",
  [VisitStatus.IN_PROGRESS]: "#FB8335",
  [VisitStatus.COMPLETED]: "#4AA33B",
  [VisitStatus.OVERDUE]: "#B1302E",
  [VisitStatus.CANCELLED]: "#000000",
};

export const getVisitColor = (
  visit: Visit,
  calendarApi?: any
): {
  status: VisitStatus;
  color: string;
} => {
  if (!visit) {
    return {
      status: VisitStatus.UN_ALLOCATED,
      color: "#9F9F9E",
    };
  }
  if (hasCheckoutPendingMoreThan72Hours(visit?.logs || [])) {
    return {
      status: VisitStatus.IN_PROGRESS,
      color: "#4682d5",
    };
  }

  if (visit?.isCancelled) {
    return {
      status: VisitStatus.CANCELLED,
      color: "#000000",
    };
  }

  if (visit.carerAssigned?.length > 0) {
    if (visit.status === VisitStatus.IN_PROGRESS) {
      return {
        status: VisitStatus.IN_PROGRESS,
        color: "#FB8335",
      };
    }
    if (visit.status === VisitStatus.COMPLETED) {
      return {
        status: VisitStatus.COMPLETED,
        color: "#4AA33B",
      };
    }
    if (visit.status === VisitStatus.OVERDUE) {
      return {
        status: VisitStatus.OVERDUE,
        color: "#B1302E",
      };
    }

    const now = dayjs().utc(true); // current time in UTC
    const visitStart = dayjs(
      visit?.endDate === "never" && visit?.origin === null
        ? calendarApi?.getDate()
        : visit.startDate
    )
      .utc()
      .set("hour", dayjs(visit.startTime).utc().hour())
      .set("minute", dayjs(visit.startTime).utc().minute());

    if (now.isAfter(visitStart)) {
      return {
        status: VisitStatus.OVERDUE,
        color: "#B1302E",
      };
    }

    return {
      status: VisitStatus.SCHEDULED,
      color: "#8824B6",
    };
  }

  return {
    status: VisitStatus.UN_ALLOCATED,
    color: "#9F9F9E",
  };
};

export const getSessionByTime = (
  maxMinDatesAccToSession: {
    [key: string]: {
      min: Dayjs;
      max: Dayjs;
    };
  },
  time: Dayjs
): string | undefined => {
  const timeWithoutDate = dayjs(time.format("HH:mm"), "HH:mm");
  return Object.entries(maxMinDatesAccToSession).find(([_, { min, max }]) =>
    timeWithoutDate.isBetween(min, max, "minute", "[]")
  )?.[0];
};

export const maxMinDatesAccToSession: {
  [key: string]: {
    min: Dayjs;
    max: Dayjs;
  };
} = {
  morning: {
    min: dayjs("00:00", "HH:mm").utc(true),
    max: dayjs("10:59", "HH:mm").utc(true),
  },
  lunch: {
    min: dayjs("11:00", "HH:mm").utc(true),
    max: dayjs("14:59", "HH:mm").utc(true),
  },
  evening: {
    min: dayjs("15:00", "HH:mm").utc(true),
    max: dayjs("17:59", "HH:mm").utc(true),
  },
  night: {
    min: dayjs("18:00", "HH:mm").utc(true),
    max: dayjs("00:00", "HH:mm").add(1, "day").utc(true),
  },
};

export const getMaxMinDatesAccToSession = (key: string) => {
  switch (key) {
    case "morning":
      return {
        min: dayjs("00:00", "HH:mm").utc(true),
        max: dayjs("10:59", "HH:mm").utc(true),
      };
    case "lunch":
      return {
        min: dayjs("11:00", "HH:mm").utc(true),
        max: dayjs("14:59", "HH:mm").utc(true),
      };
    case "evening":
      return {
        min: dayjs("15:00", "HH:mm").utc(true),
        max: dayjs("17:59", "HH:mm").utc(true),
      };
    case "night":
      return {
        min: dayjs("18:00", "HH:mm").utc(true),
        max: dayjs("00:00", "HH:mm").add(1, "day").utc(true),
      };
    default:
      return {
        min: dayjs("00:00", "HH:mm").utc(true),
        max: dayjs("00:00", "HH:mm").utc(true),
      };
  }
};

export function getSession(date: Date): string {
  // Extract the UTC hours and minutes
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();

  const timeInMinutes = hours * 60 + minutes;

  if (timeInMinutes >= 0 && timeInMinutes < 660) {
    return "morning";
  } else if (timeInMinutes >= 660 && timeInMinutes < 900) {
    return "lunch";
  } else if (timeInMinutes >= 900 && timeInMinutes < 1080) {
    return "evening";
  } else if (timeInMinutes >= 1080 && timeInMinutes <= 1440) {
    return "night";
  } else {
    return "";
  }
}

export function isDateInSession(date: Date, session: string): boolean {
  // Extract the UTC hours and minutes
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();

  const timeInMinutes = hours * 60 + minutes;

  switch (session) {
    case "morning":
      return timeInMinutes >= 0 && timeInMinutes < 660; // 06:00 - 10:59
    case "lunch":
      return timeInMinutes >= 660 && timeInMinutes < 900; // 11:00 - 14:59
    case "evening":
      return timeInMinutes >= 900 && timeInMinutes < 1080; // 15:00 - 17:59
    case "night":
      return timeInMinutes >= 1080 && timeInMinutes <= 1440; // 18:00 - 00:00
    default:
      return false; // If session is not recognized, return false
  }
}

export const numberShortener = (num: number | string) => {
  const number = Number(num);
  if (isNaN(number)) return "";
  const formatter = Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
  return formatter.format(number);
};

export const $term = (term: string | undefined) => {
  return term?.endsWith("s") ? term?.slice(0, -1) : term;
};

export const generateMonthOptions = () => {
  const months = [];

  // Start from 3 months in the future, down to 3 years ago
  const startMonth = dayjs().add(3, "month").startOf("month");
  const endMonth = dayjs().subtract(3, "year").startOf("month");

  let currentMonth = startMonth;

  while (currentMonth.isAfter(endMonth) || currentMonth.isSame(endMonth)) {
    months.push({
      value: currentMonth.format("YYYY-MM-DD"),
      label: currentMonth.format("MMMM YYYY"),
    });
    currentMonth = currentMonth.subtract(1, "month");
  }

  return months;
};

export const rruleWeekDays = {
  mo: RRule.MO,
  tu: RRule.TU,
  we: RRule.WE,
  th: RRule.TH,
  fr: RRule.FR,
  sa: RRule.SA,
  su: RRule.SU,
};

export const RRuleFrequencies: { [key: string]: number } = {
  YEARLY: 0,
  MONTHLY: 1,
  WEEKLY: 2,
  DAILY: 3,
  HOURLY: 4,
  MINUTELY: 5,
  SECONDLY: 6,
};

export const getRRuleAsObject = (visit: any) => {
  if (!visit) return {};
  const v = visit.originalRRule
    ? { ...visit, rrule: visit.originalRRule }
    : visit;
  if (!v.rrule) return {};
  const raw = v.rrule;
  const isPersisted = raw?.frequency !== undefined;
  const rawFrequency = isPersisted ? raw.frequency : raw?.freq;
  const frequency =
    rawFrequency === "custom"
      ? {
          freq:
            raw.interval === "day"
              ? RRuleFrequencies.DAILY
              : RRuleFrequencies.WEEKLY,
          interval: raw.interval !== "in-week" ? raw.intervalCount || 1 : 1,
        }
      : {
          freq:
            rawFrequency === "biweekly"
              ? RRuleFrequencies.WEEKLY
              : typeof rawFrequency === "number"
                ? rawFrequency
                : RRuleFrequencies[String(rawFrequency || "").toUpperCase()],
          interval:
            rawFrequency === "biweekly"
              ? 2
              : isPersisted
                ? raw.intervalCount || 1
                : raw.interval || 1,
        };

  const until =
    !v.endDate || v.endDate === "never" ? {} : { until: new Date(v.endDate) };

  // const weekDays = v.rrule?.byweekday?.map((day) => Object.values(rruleWeekDays)[day]);
  // ✅ Safely map weekdays to proper RRule.Weekday objects
  const validDays = raw?.byweekday ?? v.extendedProps?.rrule?.byweekday ?? [];
  const weekDays = validDays
    .map((day: any) => {
      if (day && typeof day === "object" && typeof day.weekday === "number")
        return day;
      if (typeof day === "string") {
        const normalized = day.slice(0, 2).toUpperCase(); // e.g. 'MO', 'TU'
        return (RRule as any)[normalized];
      } else if (typeof day === "number" && day >= 0 && day <= 6) {
        // convert number to RRule weekday (0 = Monday)
        return new Weekday(day);
      }
      return undefined;
    })
    .filter(Boolean);

  const byweekday =
    raw?.interval === "in-week" && weekDays.length
      ? { byweekday: weekDays }
      : {};

  const rawDtstart = raw?.dtstart;
  const startDate = v.startDate || v.start;
  const startTime = v.startTime;
  const computedDtstart = rawDtstart
    ? new Date(rawDtstart)
    : startDate
      ? new Date(startDate)
      : undefined;
  if (computedDtstart && startTime && !rawDtstart) {
    const time = new Date(startTime);
    if (!Number.isNaN(time.getTime())) {
      computedDtstart.setUTCHours(
        time.getUTCHours(),
        time.getUTCMinutes(),
        time.getUTCSeconds(),
        time.getUTCMilliseconds()
      );
    }
  }

  const rule = {
    ...frequency,
    ...until,
    ...byweekday,
    // must be a valid date
    ...(computedDtstart && !Number.isNaN(computedDtstart.getTime())
      ? { dtstart: computedDtstart }
      : {}),
  };

  return rule;
};

export const getRRule = (visit: any) => {
  const options = getRRuleAsObject(visit);
  const rruleSet = new RRuleSet();
  if ((options as any).freq === undefined || !(options as any).dtstart)
    return rruleSet;
  // Persisted endDate identifies the last scheduled day. Include its actual
  // end clock, including overnight visits, as the CMS recurrence owner does.
  if (visit.endDate && visit.endDate !== "never" && visit.endTime) {
    const start = dayjs.utc(visit.startTime);
    const end = dayjs.utc(visit.endTime);
    let until = dayjs.utc(visit.endDate);
    if (end.hour() * 60 + end.minute() <= start.hour() * 60 + start.minute())
      until = until.add(1, "day");
    (options as any).until = until
      .set("hour", end.hour())
      .set("minute", end.minute())
      .set("second", end.second())
      .set("millisecond", end.millisecond())
      .toDate();
  }
  const rule = new RRule(options as any);
  rruleSet.rrule(rule);

  if (visit.exdate?.length) {
    const dtstart = dayjs.utc((options as any).dtstart);
    visit.exdate.forEach((date) => {
      const exdate = dayjs
        .utc(date)
        .set("hour", dtstart.hour())
        .set("minute", dtstart.minute())
        .set("second", dtstart.second())
        .set("millisecond", dtstart.millisecond())
        .toDate();
      rruleSet.exdate(exdate);
    });
  }

  return rruleSet;
};

const sequence: Record<DayTime, number> = {
  morning: 0,
  lunch: 1,
  evening: 2,
  night: 3,
  "": 4,
};

export const getTaskLiveStatus = (
  task: TaskManagementResponse,
  taskDate: string
) => {
  const currentDate = new Date().toISOString().split("T")[0];
  // const currentDateStatus = task.status?.[currentDate] || "pending";
  const actualStatus = task.status?.[taskDate];

  if (actualStatus) {
    return actualStatus;
  }

  if (task.idealTime === IDEAL_TIME.ANYTIME) {
    return "pending";
  }

  if (dayjs(taskDate).isBefore(dayjs(currentDate), "D")) {
    return "overdue";
  }

  if (dayjs(taskDate).isAfter(dayjs(currentDate), "D")) {
    return "pending";
  }

  if (task.idealTime === IDEAL_TIME.SESSION) {
    // const sessions = task.dayTime;
    const sortedSessions = task.dayTime.sort(
      (a, b) => sequence[a] - sequence[b]
    );

    const lastSession = sortedSessions.at(-1) as DayTime;

    const currentTime = dayjs().utc(true);

    const morningMax = dayjs("10:59", "HH:mm").utc(true);
    const lunchMax = dayjs("14:59", "HH:mm").utc(true);
    const eveningMax = dayjs("17:59", "HH:mm").utc(true);
    const nightMax = dayjs("00:00", "HH:mm").add(1, "day").utc(true);

    const maxSessions: {
      [key: string]: Dayjs;
    } = {
      morning: morningMax,
      lunch: lunchMax,
      evening: eveningMax,
      night: nightMax,
    };

    if (currentTime.isBefore(maxSessions[lastSession])) {
      return "pending";
    }

    return "overdue";
  }

  const startTimeMinutes = dayjs(task.startTime, "HH:mm").utc().minute();
  const startTimeHours = dayjs(task.startTime, "HH:mm").utc().hour();

  const date = dayjs()
    .utc(true)
    .set("hour", startTimeHours)
    .set("minute", startTimeMinutes);

  const currentStatus = dayjs().isAfter(date) ? "overdue" : "pending";

  return currentStatus;
};

export const getSanitizedHtml = (html: string) => {
  return DOMPurify.sanitize(html);
};

export const getHaversineDistanceInMeters = ({
  lat1,
  lon1,
  lat2,
  lon2,
}: {
  lat1?: number | null;
  lon1?: number | null;
  lat2?: number | null;
  lon2?: number | null;
}): number | null => {
  // Validate inputs
  if (
    lat1 == null ||
    lon1 == null ||
    lat2 == null ||
    lon2 == null ||
    Number.isNaN(lat1) ||
    Number.isNaN(lon1) ||
    Number.isNaN(lat2) ||
    Number.isNaN(lon2)
  ) {
    return null;
  }

  const toRad = (value: number) => (value * Math.PI) / 180;

  const R = 6371e3; // Earth's radius in meters
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);

  const a =
    Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export const isWithinRadius = ({
  lat1,
  lon1,
  lat2,
  lon2,
  radiusInMeters,
}: {
  lat1?: number | null;
  lon1?: number | null;
  lat2?: number | null;
  lon2?: number | null;
  radiusInMeters: number;
}): boolean => {
  const distance = getHaversineDistanceInMeters({ lat1, lon1, lat2, lon2 });

  // If any coordinate is missing → treat as NOT within radius
  if (distance == null) return false;

  return distance <= radiusInMeters;
};

export const reverseGeocodeLatLng = async (lat: number, lng: number) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=18&lat=${lat}&lon=${lng}`
    );
    const data = await response.json();
    return data.display_name || "";
  } catch (error) {
    logger.error("Error in reverse geocoding:", error);
    return "";
  }
};

export const navigate = (path: string) => {
  if (typeof window !== "undefined") {
    Router.push(path);
  }
};

export const calculateTravelTimeMinutes = (
  from: { lat: number; lng: number } | null | undefined,
  to: { lat: number; lng: number } | null | undefined
): number | null => {
  if (!from || !to) return null;
  if (from.lat === to.lat && from.lng === to.lng) return 0;

  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(from.lat)) *
      Math.cos(toRad(to.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceKm = R * c;

  const SPEED_KMH = 25;
  const FIXED_OVERHEAD_MINS = 3;

  const travelMins = (distanceKm / SPEED_KMH) * 60 + FIXED_OVERHEAD_MINS;
  return Math.round(travelMins);
};

export const normalizeAnswerByType = (
  uiType: QuestionType,
  answer: unknown,
  options: string[] = []
): string | string[] => {
  if (uiType === QuestionType.MultipleChoice) {
    if (Array.isArray(answer)) {
      return answer.filter((item): item is string => typeof item === "string");
    }

    if (typeof answer === "string") {
      if (!answer.trim()) return [];
      return options.includes(answer)
        ? [answer]
        : answer
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }

    return [];
  }

  if (typeof answer === "string") {
    return answer;
  }

  return "";
};

export const normalizeCustomQuestions = (
  questions: any[] = []
): CustomQuestion[] => {
  return questions.map((question, index) => {
    const type = question.type || question.questionType || question.uiType;
    const mappedOptions = Array.isArray(question.options)
      ? question.options.map((option: any) =>
          typeof option === "string"
            ? option
            : option?.label || option?.value || ""
        )
      : [];

    const mappedSubQuestions = Array.isArray(question.subQuestions)
      ? question.subQuestions.map((group: any, groupIndex: number) => {
          const optionIndex = Number(group?.showOnOptionIndex);
          return {
            showOnOptionIndex: Number.isNaN(optionIndex)
              ? groupIndex
              : optionIndex,
            questions: Array.isArray(group?.questions)
              ? group.questions.map((subQuestion: any, subIndex: number) => {
                  const subType =
                    subQuestion?.uiType ||
                    subQuestion?.type ||
                    subQuestion?.questionType;
                  const subOptions = Array.isArray(subQuestion?.options)
                    ? subQuestion.options.map((option: any) =>
                        typeof option === "string"
                          ? option
                          : option?.label || option?.value || ""
                      )
                    : [];

                  return {
                    id:
                      subQuestion?.id ||
                      subQuestion?._id ||
                      `sub-${index}-${groupIndex}-${subIndex}`,
                    uiType: subType,
                    text: subQuestion?.text || subQuestion?.questionText || "",
                    subheading:
                      subQuestion?.subheading ||
                      subQuestion?.questionSubtext ||
                      "",
                    options: subOptions,
                    comment: subQuestion?.comment || "",
                    answer: normalizeAnswerByType(
                      subType,
                      subQuestion?.answer,
                      subOptions
                    ),
                  };
                })
              : [],
          };
        })
      : [];

    return {
      id: question?.id || question?._id || `custom-${Date.now()}-${index}`,
      type,
      questionText: question?.questionText || question?.text || "",
      subheading: question?.subheading || question?.questionSubtext || "",
      comment: question?.comment || "",
      answer: normalizeAnswerByType(type, question?.answer, mappedOptions),
      options: type !== QuestionType.Text ? mappedOptions : undefined,
      subQuestions: mappedSubQuestions,
    };
  });
};
export const parseAddress = (addressComponents: any[]) => {
  const address = {
    addressLine1: "",
    addressLine2: "",
    town: "",
    country: "",
    postCode: "",
  };

  let streetNumber = "";
  let route = "";

  addressComponents?.forEach((component: any) => {
    const types = Array.isArray(component?.types) ? component.types : [];
    const value =
      component?.longText ?? component?.longName ?? component?.long_name ?? "";

    if (!value) return;

    if (types.includes("street_number")) {
      streetNumber = value;
    }
    if (types.includes("route")) {
      route = value;
    }
    if (types.includes("neighborhood") || types.includes("sublocality")) {
      address.addressLine2 = value;
    }
    if (types.includes("locality") || types.includes("postal_town")) {
      address.town = value;
    }
    if (types.includes("country")) {
      address.country = value;
    }
    if (types.includes("postal_code")) {
      address.postCode = value;
    }
  });

  address.addressLine1 = [streetNumber, route].filter(Boolean).join(" ");

  return address;
};

export const hasAnyCheckoutsPendingMoreThan72Hours = (
  logs: {
    checkIn?: string | Date;
    checkOut?: string | Date | null;
  }[] = []
): boolean => {
  return logs.some(
    (log) =>
      log?.checkIn &&
      !log?.checkOut &&
      dayjs().diff(dayjs(log.checkIn), "hour") >= 72
  );
};

export const hasCheckoutPendingMoreThan72Hours = (
  logs: {
    checkIn?: string | Date;
    checkOut?: string | Date | null;
  }[] = []
): boolean => {
  // Any completed visit => false
  if (logs.some((log) => log?.checkIn && log?.checkOut)) {
    return false;
  }

  return logs.some(
    (log) =>
      log?.checkIn &&
      !log?.checkOut &&
      dayjs().diff(dayjs(log.checkIn), "hour") >= 72
  );
};

export const convertToMinutes = ({ value, unit }) => {
  switch (unit) {
    case "hours":
      return value * 60;
    case "days":
      return value * 24 * 60;
    default:
      return value;
  }
};

export const getCheckoutReasonType = ({
  setting,
  visit,
  checkOutTime,
  carerId,
}: {
  setting: any;
  visit: Visit;
  checkOutTime: Dayjs | null;
  carerId: string;
}): "early" | "late" | null => {
  const defaultThresholdMinutes = { value: 0, unit: "minutes" };
  const overrunThresholdMinutes = convertToMinutes(
    setting?.overrunException ?? defaultThresholdMinutes
  );
  const underrunThresholdMinutes = convertToMinutes(
    setting?.underrunException ?? defaultThresholdMinutes
  );

  const ownLog = (visit?.logs || []).find(
    (log: any) =>
      (typeof log.carer === "string" ? log.carer : log.carer?._id) === carerId
  );

  if (!ownLog?.checkIn) {
    return null;
  }

  let expectedDurationMinutes: number;

  if (typeof visit?.duration?.raw === "number" && visit.duration.raw > 0) {
    expectedDurationMinutes = visit.duration.raw;
  } else if (visit?.endTime) {
    expectedDurationMinutes = dayjs(visit.endTime).diff(
      dayjs(ownLog.checkIn),
      "minute"
    );
  } else {
    expectedDurationMinutes = 24 * 60;
  }

  const actualDurationMinutes = dayjs(checkOutTime).diff(
    dayjs(ownLog?.checkIn),
    "minute"
  );

  if (
    actualDurationMinutes >
    expectedDurationMinutes + overrunThresholdMinutes
  ) {
    return "late";
  }

  if (
    actualDurationMinutes <
    expectedDurationMinutes - underrunThresholdMinutes
  ) {
    return "early";
  }

  return null;
};

export const getId = <T extends { _id?: string }>(
  value: string | T | null | undefined
): string | undefined => (typeof value === "string" ? value : value?._id);

export const mergeDateAndTime = (
  date: string | Date | Dayjs,
  timeSource: string | Date | Dayjs
) => {
  const dateStr = dayjs(date).format("YYYY-MM-DD");
  return dayjs
    .utc(dateStr)
    .hour(dayjs.utc(timeSource).hour())
    .minute(dayjs.utc(timeSource).minute())
    .second(dayjs.utc(timeSource).second())
    .millisecond(dayjs.utc(timeSource).millisecond());
};

export const getAlertUser = (alert: IAlert) => {
  const serviceUserTypes = [
    ALERT_REFERENCE.SCHEDULE_VISIT.value,
    ALERT_REFERENCE.DAILY_NOTES.value,
    ALERT_REFERENCE.BODY_MAP.value,
  ];

  if (serviceUserTypes.includes(alert?.reference?.type)) {
    return alert?.reference?.data?.serviceUser;
  }

  if (alert?.reference?.type === ALERT_REFERENCE.MEDICATION.value) {
    return alert?.reference?.data?.user;
  }

  return null;
};

export const getAlertPriorityColor = (priority: string) => {
  switch (priority) {
    case ALERT_PRIORITY.LOW:
      return {
        bg: "#E8F5E9",
        color: "#2E7D32",
      };
    case ALERT_PRIORITY.MEDIUM:
      return {
        bg: "#FFF8E1",
        color: "#F9A825",
      };
    case ALERT_PRIORITY.HIGH:
      return {
        bg: "#FDECEA",
        color: "#D32F2F",
      };
    default:
      return {
        bg: "#F5F5F5",
        color: "#616161",
      };
  }
};

export const getAlertStatusColor = (status: string) => {
  switch (status) {
    case ALERT_STATUS.ACTION_NEEDED:
      return {
        bg: "#FFF3E0",
        color: "#EF6C00",
      };
    case ALERT_STATUS.IN_PROGRESS:
      return {
        bg: "#E3F2FD",
        color: "#1565C0",
      };
    case ALERT_STATUS.RESOLVED:
      return {
        bg: "#E8F5E9",
        color: "#2E7D32",
      };
    case ALERT_STATUS.DISMISSED:
      return {
        bg: "#F3E5F5",
        color: "#6A1B9A",
      };
    default:
      return {
        bg: "#F5F5F5",
        color: "#616161",
      };
  }
};

export const getAlertStatusLabel = (status: string) =>
  status === ALERT_STATUS.IN_PROGRESS ? "In progress" : status;
